/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject, Observable, catchError, combineLatest, from, map, of, shareReplay, switchMap,
} from 'rxjs';
import {
  TesterNavigationSidebarService,
  TesterNavigationSidebarUrlKey,
  TesterNavigationSidebarUrls,
  TesterNavigationSidebarUser,
} from '@ng-falcon/features/tester-navigation-sidebar/tester-navigation-sidebar.service';

// Each key lists one or more state names. A key is active when $state.includes()
// reports any entry as active — which naturally covers that state's subtree (e.g.
// listing 'profile' covers 'profile.about', 'profile.articles', etc.). Non-nested
// peer states (like 'singlearticle' alongside 'article') must be listed explicitly.
// Rules mirror the legacy nav-sidebar highlight attribute and
// NavSidebarCommunityComponent.highlightStates.
const INTERNAL_STATES: Partial<Record<TesterNavigationSidebarUrlKey, readonly string[]>> = {
  home: ['home', 'homeApp', 'root'],
  notificationsUrl: ['activity'],
  academy: [
    'academy', 'academy-home', 'tracks', 'course', 'courses',
    'track-course', 'learning-track-course', 'quiz', 'report-card',
    'learning-track-quiz', 'learning-track-report-card', 'singlecourse',
  ],
  academyCatalog: ['academy-catalog'],
  academyCertifications: ['academy-certifications', 'academy-certification-detail'],
  communityFeed: ['community', 'singlestatus', 'referralCampaignsIndex', 'referralCampaignsShow'],
  communityArticles: ['article', 'singlearticle', 'newarticle', 'updatearticle'],
  communityForum: [
    'discussion', 'singlediscussionpage', 'newDiscussion', 'editDiscussion',
    'discussioncategory', 'singlediscussion', 'forums',
  ],
  communityProfile: ['profile', 'followers', 'following'],
  projectsBoard: ['project', 'singleproject', 'projectlist'],
  moderatorReviewArticles: ['pendingarticle'],
  moderatorFlaggedContent: ['flaggedcontent'],
  moderatorTransferForums: ['transferDiscussions'],
  moderatorUrlShortener: ['urlshortener'],
};

// Tester-app paths the testerProfile rows fall back to when the user does NOT
// have TESTER_PROFILE_UX_2026. Built relative to ConfigService.config().data.profile_url.
const LEGACY_PROFILE_PATHS = {
  testerProfilePersonal: '/account/my-profile',
  testerProfileDevicesAndEnvironments: '/account/my-profile/devices',
  testerProfileAdditionalInfo: '/account/my-profile/other',
  testerProfileEarnings: '/account/payments',
  testerProfileStatistics: '/account/my-profile/statistics',
  testerProfileIdentity: '/account/my-profile/identity',
} as const;

const NEW_PROFILE_PATHS = {
  testerProfilePersonal: '/tester-profile/personal',
  testerProfileDevicesAndEnvironments: '/tester-profile/devices-and-environments',
  testerProfileAdditionalInfo: '/tester-profile/additional-info',
  testerProfileEarnings: '/tester-profile/earnings',
  testerProfileStatistics: '/tester-profile/statistics',
  testerProfileIdentity: '/tester-profile/identity',
} as const;

@Injectable({ providedIn: 'root' })
export class UtestNavigationSidebarService implements TesterNavigationSidebarService {
  readonly user$: Observable<TesterNavigationSidebarUser>;
  readonly urls$: Observable<TesterNavigationSidebarUrls>;

  private readonly currentStateName$ = new BehaviorSubject<string>('');
  private readonly isLegacyTesterProfile: boolean;
  private readonly hasAcademyCertificationsRedesign: boolean;
  private readonly hasUserIdentityVerification: boolean;

  constructor(
    // Standalone components downgraded via downgradeComponent don't share the
    // NgModule injector bridge to AngularJS, so we resolve AngularJS services
    // lazily from $injector instead of injecting by name.
    @Inject('$injector') private $injector: any,
  ) {
    this.isLegacyTesterProfile = !this.featureFlags.isOn('TESTER_PROFILE_UX_2026');
    this.hasAcademyCertificationsRedesign = this.featureFlags.isOn('UTEST_ACADEMY_REDESIGN_CERTIFICATIONS');
    this.hasUserIdentityVerification = this.featureFlags.isOn('USER_IDENTITY_VERIFICATION');

    // Resolve user and config independently — without catchError on each, a
    // single failure would error out user$ / urls$ and leave the sidebar empty
    // for the rest of the session. Handling them separately (rather than via
    // Promise.all) means a config failure doesn't discard a successful user
    // response, which would otherwise wipe isTester and hide Support.
    const userRes$ = from(this.userService.me()).pipe(
      catchError(() => of(null as any)),
    );
    const configRes$ = from(this.configService.config()).pipe(
      catchError(() => of(null as any)),
    );

    const user = combineLatest([userRes$, configRes$]).pipe(
      map(([userRes, configRes]: any[]) => ({
        slug: userRes?.data?.slug ?? null,
        isModerator: userRes?.data?.user_type === 'moderator' || userRes?.data?.user_type === 'admin',
        isTester: userRes?.data?.is_tester === true,
        profileUrl: configRes?.data?.profile_url ?? '',
        chatUrl: configRes?.data?.chat_url ?? '',
      })),
      shareReplay(1),
    );

    this.user$ = user.pipe(map(({ slug, isModerator, isTester }) => ({
      slug,
      isModerator,
      isLegacyTesterProfile: this.isLegacyTesterProfile,
      hasAcademyCertificationsRedesign: this.hasAcademyCertificationsRedesign,
      hasUserIdentityVerification: this.hasUserIdentityVerification,
      isByot: false,
      isTester,
    })));
    this.urls$ = user.pipe(map(({ slug, profileUrl, chatUrl }) => this.buildUrls(slug, profileUrl, chatUrl)));

    this.currentStateName$.next(this.$state.current?.name ?? '');
    this.$transitions.onSuccess({}, (transition: any) => {
      this.currentStateName$.next(transition.to()?.name ?? '');
    });
  }

  isActive$(key: TesterNavigationSidebarUrlKey): Observable<boolean> {
    const states = INTERNAL_STATES[key];
    if (!states || states.length === 0) {
      return this.currentStateName$.pipe(map(() => false));
    }
    if (key === 'communityProfile') {
      return this.user$.pipe(
        switchMap(user => this.currentStateName$.pipe(
          map(name => {
            const segments = name.split('.');
            // followers/following pages always highlight Community Profile
            // regardless of whose profile is being viewed.
            const isFollowersOrFollowing = segments.includes('followers') || segments.includes('following');
            return isFollowersOrFollowing
              || (this.matchesStateSegments(name, states) && this.$state.params?.profileId === user.slug);
          }),
        )),
      );
    }
    return this.currentStateName$.pipe(
      map(name => this.matchesStateSegments(name, states)),
    );
  }

  // Segment-intersection match: split the dotted state name and look for any
  // overlap with the listed entries. Mirrors the legacy NavSidebarItemCtrl
  // (`_.intersection(state.split('.'), highlight.split(','))`) and crucially
  // avoids $state.includes() matching every descendant of a parent like
  // 'root' — which would otherwise highlight Home on every route.
  private matchesStateSegments(stateName: string, states: readonly string[]): boolean {
    if (!stateName) {
      return false;
    }
    const segments = stateName.split('.');
    return states.some(s => segments.includes(s));
  }

  private get userService(): any { return this.$injector.get('UserService'); }
  private get configService(): any { return this.$injector.get('ConfigService'); }
  private get $state(): any { return this.$injector.get('$state'); }
  private get $transitions(): any { return this.$injector.get('$transitions'); }
  private get featureFlags(): any { return this.$injector.get('featureFlags'); }

  private buildUrls(slug: string | null, profileUrl: string, chatUrl: string): TesterNavigationSidebarUrls {
    const tester = (path: string): string => `${profileUrl}${path}`;
    const internal = (stateName: string): string => this.$state.href(stateName) ?? '';
    const profilePaths = this.isLegacyTesterProfile ? LEGACY_PROFILE_PATHS : NEW_PROFILE_PATHS;

    return {
      home: '/',
      chatUrl,
      notificationsUrl: internal('activity') || '/activity',
      // testerProfileSection is an active-state-only key for the parent row.
      // utest-fe has no tester-app account.* routes, so isActive$ returns
      // false for it here — the parent's children-driven highlighting still
      // works for the rows utest-fe does navigate to.
      testerProfileSection: '',
      testerProfilePersonal: tester(profilePaths.testerProfilePersonal),
      testerProfileDevicesAndEnvironments: tester(profilePaths.testerProfileDevicesAndEnvironments),
      testerProfileAdditionalInfo: tester(profilePaths.testerProfileAdditionalInfo),
      testerProfileEarnings: tester(profilePaths.testerProfileEarnings),
      testerProfileStatistics: tester(profilePaths.testerProfileStatistics),
      testerProfileIdentity: this.hasUserIdentityVerification ? tester(profilePaths.testerProfileIdentity) : '',
      projectsOverview: tester('/dashboard'),
      projectsTestCycles: tester('/testcycles'),
      projectsIssues: tester('/issues'),
      projectsTestCases: tester('/testcases'),
      // FLO-54198: resolve through $state.href so the links keep the <base href> (/utest/ on the tester-app host).
      projectsBoard: internal('project') || '/projects',
      academy: internal('academy') || '/academy',
      academyCatalog: internal('academy-catalog') || '/academy/catalog',
      academyCertifications: internal('academy-certifications') || '/academy/certifications',
      communityFeed: internal('community') || '/community',
      communityArticles: internal('article') || '/articles',
      communityForum: internal('discussion') || '/forums',
      communityProfile: slug
        ? (this.$state.href('profile.about', { profileId: slug }) ?? `/profile/${encodeURIComponent(slug)}/about`)
        : '/',
      moderatorReviewArticles: internal('pendingarticle') || '/pending-articles',
      moderatorFlaggedContent: internal('flaggedcontent') || '/flagged-content',
      moderatorTransferForums: internal('transferDiscussions') || '/transfer/forums',
      moderatorUrlShortener: internal('urlshortener') || '/url-shortener',
    };
  }
}
